import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { stripVariableRowIds } from '../../utils/collectionTabState';
import { normalizeAuth } from '../../utils/requestAuth';
import { isModKey } from '../../utils/keyboard';

export function makeKvRowId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function ensureTrailingVarRow(rows, prefix) {
  const base = rows?.length ? rows.map((r) => ({ ...r })) : [];
  const last = base[base.length - 1];
  if (!last || String(last.key ?? '').trim() !== '') {
    base.push({ key: '', value: '', enabled: true, _id: makeKvRowId(prefix) });
  }
  return base;
}

function seedFrom(initialData, { fallbackName, inheritAuth, rowPrefix }) {
  return {
    name: initialData?.name || fallbackName,
    section: initialData?.section || 'docs',
    documentation: initialData?.documentation || '',
    auth: normalizeAuth(initialData?.auth, { forRequest: inheritAuth }),
    variables: ensureTrailingVarRow(
      initialData?.variables?.map((v, i) => ({ ...v, _id: v._id || `${rowPrefix}-${i}-${Date.now()}` })),
      rowPrefix
    ),
    preRequestScript: initialData?.preRequestScript || '',
    testScript: initialData?.testScript || '',
  };
}

/** The saved/notified shape: the whole page, never a subset (a subset save blanks the rest). */
function payloadOf(state) {
  return {
    section: state.section,
    documentation: state.documentation,
    auth: state.auth,
    variables: stripVariableRowIds(state.variables),
    preRequestScript: state.preRequestScript,
    testScript: state.testScript,
  };
}

/**
 * The draft behind a collection OR folder page (`kind`), extracted from
 * CollectionEditor on 28.09.2026 so both pages share one model (owner D3):
 * a tab-held draft, Save / Ctrl+S for everything, and variables that save
 * themselves on a pause.
 */
export function useCollectionPageDraft({
  initialData,
  onDataChange,
  onSave,
  canEdit,
  onVariableUpdate,
  kind = 'collection',
}) {
  // A folder's auth may be "Inherit from parent"; a collection IS the parent.
  const inheritAuth = kind === 'folder';
  const fallbackName = inheritAuth ? 'Folder' : 'Collection';
  const rowPrefix = inheritAuth ? 'fv' : 'cv';
  const opts = { fallbackName, inheritAuth, rowPrefix };
  const [local, setLocal] = useState(() => seedFrom(initialData, opts));

  useEffect(() => {
    if (!initialData?.collectionId) return;
    setLocal(seedFrom(initialData, opts));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reseeds the draft only when a different collection/folder is opened (or renamed); the other fields are what the user is editing, so tracking them would discard their edits
  }, [initialData?.collectionId, initialData?.folderId, initialData?.name]);

  // A folder chip that opens THIS (already open) page on another section.
  useEffect(() => {
    const wanted = initialData?.section;
    if (wanted) setLocal((prev) => (prev.section === wanted ? prev : { ...prev, section: wanted }));
  }, [initialData?.section]);

  /**
   * Variables save themselves, shortly after you stop editing.
   *
   * Everything else on this tab waits for the Save button, and for variables
   * that was the wrong trade: a variable is a value the REQUEST reads, so until
   * you remembered to press Save the app went on resolving the old one. The
   * request's own Variables panel already saves on a pause for exactly this
   * reason (owner, 23.09.2026). Slower than the panel's 500ms: this one crosses
   * the network.
   */
  const AUTOSAVE_MS = 800;
  const autosaveTimer = useRef(null);
  const pendingSave = useRef(null);
  // The handler through a ref: the flush below runs on unmount, when the
  // closure that scheduled it is already gone.
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const flushVariablesSave = useCallback(() => {
    const next = pendingSave.current;
    pendingSave.current = null;
    if (autosaveTimer.current) {
      clearTimeout(autosaveTimer.current);
      autosaveTimer.current = null;
    }
    if (!next) return;
    onSaveRef.current?.({
      // The WHOLE page rides along: the save replaces every field together.
      ...payloadOf(next),
      // Named explicitly: this can land after the user has moved to another
      // tab, and the save must not follow them there.
      tabId: initialData?.id,
      collectionId: initialData?.collectionId,
      silent: true,
    });
  }, [initialData?.id, initialData?.collectionId]);

  const scheduleVariablesSave = useCallback(
    (next) => {
      if (!canEdit) return;
      pendingSave.current = next;
      clearTimeout(autosaveTimer.current);
      autosaveTimer.current = setTimeout(flushVariablesSave, AUTOSAVE_MS);
    },
    [canEdit, flushVariablesSave]
  );

  // Anything still pending when the tab closes is written, not dropped.
  useEffect(() => () => flushVariablesSave(), [flushVariablesSave]);

  const notifyParent = useCallback(
    (next) => onDataChange?.({ ...payloadOf(next), hasUnsavedChanges: true }),
    [onDataChange]
  );

  const setSection = (section) => {
    setLocal((prev) => {
      if (prev.section === section) return prev;
      const next = { ...prev, section };
      onDataChange?.(payloadOf(next));
      return next;
    });
  };

  const patchLocal = (patch) => {
    setLocal((prev) => {
      const next = { ...prev, ...patch };
      notifyParent(next);
      return next;
    });
  };

  const updateAuth = (patch) => {
    setLocal((prev) => {
      const next = { ...prev, auth: normalizeAuth({ ...prev.auth, ...patch }, { forRequest: inheritAuth }) };
      notifyParent(next);
      return next;
    });
  };

  const setVariables = (produce) => {
    setLocal((prev) => {
      const next = { ...prev, variables: produce(prev.variables) };
      notifyParent(next);
      scheduleVariablesSave(next);
      return next;
    });
  };

  /** `patch` is an object merged into the row — see VariableKvTable's onUpdate. */
  const updateVariable = (index, patch) =>
    setVariables((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const removeVariable = (index) =>
    setVariables((rows) => ensureTrailingVarRow(rows.filter((_, i) => i !== index), rowPrefix));

  /** Defer trailing-row append so SyntaxHighlightInput keeps caret stable (same as RequestBuilder params). */
  const handleVariableKeyChange = (index, value) => {
    setLocal((prev) => {
      const rows = [...prev.variables];
      const prevKey = rows[index].key;
      rows[index] = { ...rows[index], key: value };
      const shouldAppendTrailing = index === prev.variables.length - 1 && prevKey === '' && value.length > 0;
      const next = { ...prev, variables: rows };
      notifyParent(next);
      scheduleVariablesSave(next);
      if (shouldAppendTrailing) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setLocal((p) => {
              const last = p.variables[p.variables.length - 1];
              if (!last || last.key === '') return p;
              const appended = {
                ...p,
                variables: [...p.variables, { key: '', value: '', enabled: true, _id: makeKvRowId(rowPrefix) }],
              };
              notifyParent(appended);
              return appended;
            });
          });
        });
      }
      return next;
    });
  };

  const variablesForInput = useMemo(() => stripVariableRowIds(local.variables), [local.variables]);

  /**
   * A variable saved from the editor popup while THIS page is open, for THIS
   * page's own scope (collection page only — a folder chip never opens the
   * popup). The draft would otherwise overwrite it a keystroke later
   * (owner, 23.09.2026), so it is folded into the draft instead. Anything
   * else goes to the shared handler untouched.
   */
  const handleInlineVariableUpdate = useCallback(async (variable, updateContext = {}) => {
    const scope = typeof updateContext === 'string' ? updateContext : updateContext.scope;
    const sameCollection =
      String(updateContext?.collectionId ?? '') === String(initialData?.collectionId ?? '');
    if (kind !== 'collection' || scope !== 'collection' || !sameCollection || !variable?.key) {
      return onVariableUpdate?.(variable, updateContext);
    }
    setLocal((prev) => {
      const rows = [...prev.variables];
      const at = rows.findIndex((r) => (r.key ?? '').toLowerCase() === (variable.key ?? '').toLowerCase());
      const base = at >= 0 ? rows[at] : { _id: makeKvRowId(rowPrefix), enabled: true };
      const row = { ...base, key: variable.key, value: variable.value };
      // Assigned rather than spread so an emptied list actually clears.
      delete row.options;
      if (variable.options?.length) row.options = variable.options;
      if (at >= 0) rows[at] = row;
      else rows.splice(Math.max(rows.length - 1, 0), 0, row); // before the trailing blank
      const next = { ...prev, variables: ensureTrailingVarRow(rows, rowPrefix) };
      notifyParent(next);
      scheduleVariablesSave(next);
      return next;
    });
    return undefined;
  }, [initialData?.collectionId, onVariableUpdate, notifyParent, scheduleVariablesSave, kind, rowPrefix]);

  const handleSave = useCallback(() => {
    pendingSave.current = null;
    clearTimeout(autosaveTimer.current);
    onSave?.({ ...payloadOf(local), tabId: initialData?.id, collectionId: initialData?.collectionId });
  }, [onSave, local, initialData?.id, initialData?.collectionId]);

  useEffect(() => {
    if (!canEdit) return undefined;
    const handleKeyDown = (e) => {
      if (isModKey(e) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [canEdit, handleSave]);

  return {
    local,
    setSection,
    updateAuth,
    updateVariable,
    removeVariable,
    handleVariableKeyChange,
    handleInlineVariableUpdate,
    setDocumentation: (html) => patchLocal({ documentation: html }),
    setScript: (key, text) => patchLocal({ [key]: text }),
    variablesForInput,
    handleSave,
  };
}
